import { Building2, CreditCard, Lock, Printer, Wallet } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { recaptchaHeader, recaptchaToken } from '@/auth/recaptcha';
import { TituloBloque } from '@/components/BloquesConsalud';
import { AVATARES } from '@/lib/avatares';
import { fechaLarga, mxn } from '@/lib/formato';
import { useCountUp } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { PayPalBotones } from './PayPalBotones';
import { useCotizacion, useMetodosPago } from './queries';

type Metodo = 'stripe' | 'paypal' | 'paypal-tarjeta' | 'transferencia';

function Opcion({
  activo,
  onSelect,
  icono,
  titulo,
  detalle,
}: {
  activo: boolean;
  onSelect: () => void;
  icono: ReactNode;
  titulo: string;
  detalle: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={activo}
      onClick={onSelect}
      className={cn(
        'card-vidrio flex items-center gap-3 p-4 text-left transition-shadow',
        activo && 'shadow-[var(--shadow-brillo-cian)]',
      )}
    >
      <span
        className={cn(
          'grid h-10 w-10 place-items-center rounded-full',
          activo ? 'bg-azul-600 text-white' : 'bg-[var(--vidrio-fuerte)] text-texto-2',
        )}
      >
        {icono}
      </span>
      <span>
        <span className="block font-extrabold text-texto">{titulo}</span>
        <span className="block text-xs text-texto-2">{detalle}</span>
      </span>
    </button>
  );
}

/** Pasos ilustrados del depósito (columna derecha de `inicio/pagar` con la opción 1). */
const PASOS_DEPOSITO = [
  { src: '/img/pagos/paso-1-licencia.webp', alt: '1. Elige tu licencia' },
  { src: '/img/pagos/paso-2-deposita.webp', alt: '2. Deposita o transfiere' },
  { src: '/img/pagos/paso-3-comprobante.webp', alt: '3. Envía tu comprobante' },
  { src: '/img/pagos/paso-4-verificacion.webp', alt: '4. Activamos tu licencia' },
];

/** Ilustración del método elegido (las del modal de licencias del legacy), sobre fondo blanco. */
function Ilustracion({ metodo, banco }: { metodo: Metodo; banco: string }) {
  if (metodo === 'transferencia') {
    // El paso 2 muestra el logo de BBVA: solo si la cuenta configurada es de ese banco.
    const pasos = PASOS_DEPOSITO.filter((_, i) => i !== 1 || /bbva|bancomer/i.test(banco));
    return (
      <ol className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4 print:hidden">
        {pasos.map((p) => (
          <li key={p.src} className="rounded-xl bg-white p-1.5">
            <img src={p.src} alt={p.alt} loading="lazy" className="w-full" />
          </li>
        ))}
      </ol>
    );
  }
  return (
    <img
      src={metodo === 'paypal' ? '/img/pagos/pago-paypal.webp' : '/img/pagos/pago-tarjeta.webp'}
      alt=""
      className="mx-auto mb-5 w-full max-w-[15rem] rounded-xl bg-white p-2"
    />
  );
}

/** `/pagar/:membershipId` (antes `inicio/pagar/`): resumen calculado por la API + método de pago. */
export function PagarPage() {
  const params = useParams();
  const membershipId = Number(params.membershipId);
  const [search] = useSearchParams();
  const becaId = Number(search.get('beca')) || undefined;
  const navigate = useNavigate();
  const [cupon, setCupon] = useState('');
  const [cuponAplicado, setCuponAplicado] = useState('');
  const [enviando, setEnviando] = useState(false);
  const {
    data: q,
    isError,
    error,
    isFetching,
  } = useCotizacion(membershipId, cuponAplicado, becaId);
  const { data: metodos } = useMetodosPago();
  const [eleccion, setMetodo] = useState<Metodo | null>(null);
  const metodo: Metodo | null =
    eleccion ??
    (metodos
      ? metodos.stripe.enabled
        ? 'stripe'
        : metodos.paypal.enabled
          ? 'paypal'
          : 'transferencia'
      : null);
  const total = useRef<HTMLElement>(null);
  useCountUp(total, Number(q?.finalPrice ?? 0), {
    from: Number(q?.regularPrice ?? 0),
    currency: true,
  });

  useEffect(() => {
    if (search.get('cancelado'))
      toast.info('Cancelaste el pago. Puedes intentarlo de nuevo cuando quieras.');
  }, [search]);
  useEffect(() => {
    if (q?.coupon)
      (q.coupon.status === 'applied' ? toast.success : toast.warning)(q.coupon.message, {
        id: 'cupon',
      });
  }, [q?.coupon]);

  if (isError) {
    return (
      <section className="panel-vidrio mx-auto max-w-xl text-center">
        <h1 className="text-xl font-extrabold text-azul-texto">No pudimos preparar tu compra</h1>
        <p className="mt-2 text-texto-2">{mensajeDe(error)}</p>
        <Link to="/licencias" className="btn-cs btn-primario mt-6">
          Ver licencias
        </Link>
      </section>
    );
  }

  const cuerpo = {
    membershipId,
    ...(q?.coupon?.status === 'applied' ? { couponCode: q.coupon.code } : {}),
    ...(becaId ? { scholarshipId: becaId } : {}),
  };

  const pagarConTarjeta = async () => {
    setEnviando(true);
    try {
      const token = await recaptchaToken('checkout');
      const r = await unwrap<{ url: string }>(
        api.POST('/api/v1/payments/stripe/checkout-sessions', {
          body: cuerpo,
          headers: recaptchaHeader(token),
        }),
      );
      window.location.assign(r.url);
    } catch (err) {
      toast.error(mensajeDe(err));
      setEnviando(false);
    }
  };

  const conDescuento = q && q.finalPrice !== q.regularPrice;

  return (
    <div className="grid gap-6 pt-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <section className="panel-vidrio">
        <TituloBloque avatar={AVATARES.doctor}>Resumen de la compra</TituloBloque>
        <h1 className="mt-5 text-2xl font-extrabold text-texto">{q?.name ?? 'Cargando…'}</h1>
        {q && (
          <dl className="mt-4 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-texto-2">Precio regular</dt>
              <dd
                className={cn('tabular', conDescuento ? 'text-texto-3 line-through' : 'font-bold')}
              >
                {mxn(q.regularPrice)} MXN
              </dd>
            </div>
            {conDescuento && (
              <>
                <div className="flex justify-between">
                  <dt className="text-texto-2">Descuento</dt>
                  <dd className="tabular">{q.discountPercent}%</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-texto-2">Ahorrarás</dt>
                  <dd className="font-bold text-verde-texto tabular">{mxn(q.savings)} MXN</dd>
                </div>
              </>
            )}
            {q.scholarship && (
              <p className="insignia insignia-activa mt-1">Beca: {q.scholarship.name}</p>
            )}
            <div className="mt-3 flex items-end justify-between border-t border-[var(--separador)] pt-3">
              <dt className="font-bold text-texto">Total</dt>
              <dd className="text-3xl font-extrabold text-cian-texto tabular">
                <span ref={total}>{mxn(q.finalPrice)}</span>{' '}
                <span className="text-sm text-texto-2">MXN</span>
              </dd>
            </div>
            <p className="text-xs text-texto-3">
              Vigencia: {q.durationMonths} meses (del {fechaLarga(q.startsOn)} al{' '}
              {fechaLarga(q.endsOn)}).
            </p>
          </dl>
        )}
        {!becaId && (
          <form
            className="mt-5 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setCuponAplicado(cupon.trim().toUpperCase());
            }}
          >
            <label className="flex-1">
              <span className="sr-only">Código promocional</span>
              <input
                value={cupon}
                onChange={(e) => setCupon(e.target.value)}
                placeholder="¿Tienes un código promocional?"
                className="input-vidrio"
                maxLength={30}
              />
            </label>
            <button
              type="submit"
              className="btn-cs btn-contorno"
              disabled={!cupon.trim() || isFetching}
            >
              Aplicar
            </button>
          </form>
        )}
        {q?.description && (
          <>
            <h2 className="mt-6 font-extrabold text-azul-texto">Características</h2>
            <ul className="mt-2 space-y-1 text-sm text-texto-2">
              {q.description
                .split(/\r?\n/)
                .map((l) => l.replace(/^[-•*\s]+/, '').trim())
                .filter(Boolean)
                .map((l) => (
                  <li key={l}>✓ {l}</li>
                ))}
            </ul>
          </>
        )}
        <img
          src="/img/consalud-horizontal-color.png"
          alt="Consalud"
          className="solo-claro mx-auto mt-8 h-9 w-auto"
        />
        <img
          src="/img/consalud-horizontal-blanco.png"
          alt="Consalud"
          className="solo-oscuro mx-auto mt-8 h-9 w-auto"
        />
      </section>

      <section className="panel-vidrio">
        <TituloBloque icono={<CreditCard size={18} />}>Elige cómo pagar</TituloBloque>
        <div
          role="radiogroup"
          aria-label="Método de pago"
          className="mt-4 grid gap-3 sm:grid-cols-3"
        >
          {metodos?.stripe.enabled && (
            <Opcion
              activo={metodo === 'stripe'}
              onSelect={() => setMetodo('stripe')}
              icono={<CreditCard size={20} />}
              titulo="Tarjeta (Stripe)"
              detalle="Crédito o débito · MSI"
            />
          )}
          {metodos?.paypal.enabled && (
            <Opcion
              activo={metodo === 'paypal'}
              onSelect={() => setMetodo('paypal')}
              icono={<Wallet size={20} />}
              titulo="PayPal"
              detalle="Con tu cuenta PayPal"
            />
          )}
          {metodos?.paypal.enabled && (
            <Opcion
              activo={metodo === 'paypal-tarjeta'}
              onSelect={() => setMetodo('paypal-tarjeta')}
              icono={<CreditCard size={20} />}
              titulo="Tarjeta"
              detalle="Crédito o débito vía PayPal, sin cuenta"
            />
          )}
          {metodos?.transfer.enabled && (
            <Opcion
              activo={metodo === 'transferencia'}
              onSelect={() => setMetodo('transferencia')}
              icono={<Building2 size={20} />}
              titulo="Depósito"
              detalle="Transferencia o ventanilla"
            />
          )}
        </div>

        <div className="mt-6">
          {metodo && q && (metodo !== 'transferencia' || metodos?.transfer.enabled) && (
            <Ilustracion metodo={metodo} banco={metodos?.transfer.bank ?? ''} />
          )}
          {metodo === 'stripe' && q && (
            <div className="text-center">
              <img
                src="/img/pagos/tarjetas.png"
                alt="Visa, Mastercard, American Express y Discover"
                className="mx-auto h-8 w-auto rounded bg-white p-1"
              />
              <p className="mt-3 text-sm text-texto-2">
                Te llevaremos a Stripe para capturar tu tarjeta de forma segura. Consalud ENARM
                nunca ve ni guarda tus datos de tarjeta.
              </p>
              <p className="vidrio-tinte mt-3 rounded-xl p-3 text-xs text-texto">
                <strong>Meses sin intereses</strong> con tarjetas de crédito participantes emitidas
                en México. Los plazos aparecen en la pantalla de pago.
              </p>
              <button
                type="button"
                className="btn-cs btn-primario mt-5 px-8 py-3 text-base"
                disabled={enviando}
                onClick={() => void pagarConTarjeta()}
              >
                <Lock size={18} />{' '}
                {enviando ? 'Conectando con Stripe…' : `Pagar ${mxn(q.finalPrice)}`}
              </button>
              {metodos?.stripe.testMode && (
                <p className="mt-2 text-xs text-texto-3">
                  Modo de prueba: no se hace ningún cargo real.
                </p>
              )}
            </div>
          )}
          {(metodo === 'paypal' || metodo === 'paypal-tarjeta') &&
            metodos?.paypal.clientId &&
            q && (
              <PayPalBotones
                fuente={metodo === 'paypal-tarjeta' ? 'card' : 'paypal'}
                clientId={metodos.paypal.clientId}
                body={cuerpo}
                onPagado={(folio) =>
                  void navigate(`/pagar/resultado?folio=${encodeURIComponent(folio)}`)
                }
                onError={(m) => toast.error(m)}
              />
            )}
          {metodo === 'transferencia' && metodos?.transfer.enabled && q && (
            <div className="superficie-lectura p-5">
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-extrabold text-texto">Depósito bancario o transferencia</h3>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="btn-cs btn-vidrio px-3 py-1.5 text-xs print:hidden"
                >
                  <Printer size={14} /> Imprimir
                </button>
              </div>
              <p className="mt-2 text-sm text-texto-2">
                Deposita en la siguiente cuenta y envíanos tu comprobante:
              </p>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="font-bold">Banco</dt>
                <dd>{metodos.transfer.bank}</dd>
                <dt className="font-bold">Cuenta</dt>
                <dd className="tabular">{metodos.transfer.account}</dd>
                <dt className="font-bold">CLABE</dt>
                <dd className="tabular">{metodos.transfer.clabe}</dd>
                <dt className="font-bold">Titular</dt>
                <dd>{metodos.transfer.holder}</dd>
                <dt className="font-bold">Correo</dt>
                <dd>{metodos.transfer.email}</dd>
              </dl>
              <p className="mt-4 text-center text-lg">
                Cantidad a pagar: <strong className="tabular">{mxn(q.finalPrice)} MXN</strong>
              </p>
              <p className="mt-2 text-center text-xs text-texto-3">
                Envía tu comprobante a {metodos.transfer.email} o por WhatsApp al{' '}
                <a
                  href="https://api.whatsapp.com/send?l=es&phone=525545204941"
                  target="_blank"
                  rel="noreferrer"
                >
                  +52 55 4520 4941
                </a>
                . Activamos tu licencia al confirmar el depósito.
              </p>
            </div>
          )}
          {metodos &&
            !metodos.stripe.enabled &&
            !metodos.paypal.enabled &&
            !metodos.transfer.enabled && (
              <p className="text-texto-2">
                Por el momento no hay métodos de pago disponibles. Escríbenos por WhatsApp.
              </p>
            )}
        </div>
      </section>
    </div>
  );
}
